/**
 * Unequal (Futoshiki) Puzzle Generator
 *
 * Generates puzzles with unique solutions at specified difficulty levels.
 */

import {
  GameMode,
  GameState,
  Difficulty,
  CellFlags,
  DIRECTIONS,
  ClueType,
  PRESETS,
} from './types';
import { generateLatinSquare, shuffle } from './latinSquare';
import { solveState, createSolverContext } from './solver';

const MAX_TRIES = 50;

/**
 * Create empty game state
 */
export function createEmptyState(order: number, mode: GameMode): GameState {
  const o2 = order * order;
  return {
    order,
    mode,
    completed: false,
    cheated: false,
    nums: new Array<number>(o2).fill(0),
    hints: new Array<boolean>(o2 * order).fill(false),
    flags: new Array<number>(o2).fill(0),
    solution: [],
    selectedX: 0,
    selectedY: 0,
  };
}

/**
 * Clone a game state
 */
export function cloneState(state: GameState): GameState {
  return {
    ...state,
    nums: [...state.nums],
    hints: [...state.hints],
    flags: [...state.flags],
    solution: [...state.solution],
  };
}

/**
 * Initialize clue candidates array
 * Each cell has 5 potential clues: number (4) + 4 directions (0-3)
 */
function initClues(order: number): number[] {
  const o2 = order * order;
  const clues: number[] = [];

  for (let i = 0; i < o2 * 5; i++) {
    // Encode as position * 5 + clueType
    // ClueType 4 = number, 0-3 = directions
    const pos = i % o2;
    const type = 4 - Math.floor(i / o2);
    clues.push(pos * 5 + type);
  }

  // Shuffle numbers separately from constraints
  const numbers = clues.slice(0, o2);
  const constraints = clues.slice(o2);
  shuffle(numbers);
  shuffle(constraints);

  return [...numbers, ...constraints];
}

/**
 * Check if a clue can be placed
 */
function canPlaceClue(
  state: GameState,
  clueCode: number,
  solution: number[],
  dryRun: boolean
): boolean {
  const position = Math.floor(clueCode / 5);
  const clueType = clueCode % 5;
  const { order, mode } = state;

  const x = position % order;
  const y = Math.floor(position / order);

  if (clueType === ClueType.NUMBER) {
    // Number clue
    if ((state.nums[y * order + x] ?? 0) !== 0) {
      return false; // Already has number
    }

    if (!dryRun) {
      state.nums[y * order + x] = solution[y * order + x] ?? 0;
      state.flags[y * order + x] = (state.flags[y * order + x] ?? 0) | CellFlags.IMMUTABLE;
    }

    return true;
  } else {
    // Constraint clue (direction 0-3)
    const dir = DIRECTIONS[clueType];
    if (!dir) return false;

    const nx = x + dir.dx;
    const ny = y + dir.dy;

    // Check bounds
    if (nx < 0 || nx >= order || ny < 0 || ny >= order) {
      return false;
    }

    // Check if constraint already exists
    const currentFlags = state.flags[y * order + x] ?? 0;
    if (currentFlags & dir.flag) {
      return false;
    }

    // Verify constraint is valid for solution (BEFORE dryRun check)
    const thisValue = solution[y * order + x] ?? 0;
    const neighborValue = solution[ny * order + nx] ?? 0;

    if (mode === GameMode.ADJACENT) {
      // Values must differ by exactly 1
      if (Math.abs(thisValue - neighborValue) !== 1) {
        return false;
      }
    } else {
      // MODE_UNEQUAL: this cell must be > neighbor
      if (thisValue <= neighborValue) {
        return false;
      }
    }

    if (!dryRun) {
      // Place constraint on this cell only
      // In UNEQUAL mode, the flag means "this cell > neighbor"
      // We don't set oppositeFlag because that would imply "neighbor > this cell"
      // which is a contradiction
      state.flags[y * order + x] = (state.flags[y * order + x] ?? 0) | dir.flag;

      // In ADJACENT mode, constraints are symmetric, so set both directions
      if (mode === GameMode.ADJACENT) {
        state.flags[ny * order + nx] = (state.flags[ny * order + nx] ?? 0) | dir.oppositeFlag;
      }
    }

    return true;
  }
}

/**
 * Remove a clue from the state
 */
function removeClue(
  state: GameState,
  clueCode: number,
  dryRun: boolean
): boolean {
  const position = Math.floor(clueCode / 5);
  const clueType = clueCode % 5;
  const { order } = state;

  const x = position % order;
  const y = Math.floor(position / order);

  if (clueType === ClueType.NUMBER) {
    // Remove number clue
    if ((state.nums[y * order + x] ?? 0) === 0) {
      return false; // No number to remove
    }

    if (!dryRun) {
      state.nums[y * order + x] = 0;
      state.flags[y * order + x] = (state.flags[y * order + x] ?? 0) & ~CellFlags.IMMUTABLE;
    }

    return true;
  } else {
    // Remove constraint
    const dir = DIRECTIONS[clueType];
    if (!dir) return false;

    const currentFlags = state.flags[y * order + x] ?? 0;
    if (!(currentFlags & dir.flag)) {
      return false; // Constraint doesn't exist
    }

    if (!dryRun) {
      const nx = x + dir.dx;
      const ny = y + dir.dy;

      // Remove constraint from this cell
      state.flags[y * order + x] = (state.flags[y * order + x] ?? 0) & ~dir.flag;

      // In ADJACENT mode, also remove from neighbor
      if (state.mode === GameMode.ADJACENT) {
        state.flags[ny * order + nx] = (state.flags[ny * order + nx] ?? 0) & ~dir.oppositeFlag;
      }
    }

    return true;
  }
}

/**
 * Select best clue to add using heuristic
 */
function selectBestClue(
  state: GameState,
  clues: number[],
  solution: number[]
): number {
  const { order } = state;
  let maxPossibilities = 0;
  let minClues = 5;
  let bestIndex = -1;

  // Create solver context to check possibilities
  const ctx = createSolverContext(state);

  // Iterate through clue candidates (reverse order for efficiency)
  for (let i = clues.length - 1; i >= 0; i--) {
    const clueCode = clues[i];
    if (clueCode === undefined) continue;

    if (!canPlaceClue(state, clueCode, solution, true)) {
      continue;
    }

    const position = Math.floor(clueCode / 5);
    const x = position % order;
    const y = Math.floor(position / order);

    // Count remaining possibilities for this cell
    let possibilities = 0;
    for (let n = 0; n < order; n++) {
      if (ctx.cube[y]?.[x]?.[n]) {
        possibilities++;
      }
    }

    // Count existing constraints at this location
    let existingClues = 0;
    const currentFlags = state.flags[y * order + x] ?? 0;
    for (let d = 0; d < 4; d++) {
      const dir = DIRECTIONS[d];
      if (dir && (currentFlags & dir.flag)) {
        existingClues++;
      }
    }

    // Prefer cells with more possibilities, then fewer existing clues
    if (
      possibilities > maxPossibilities ||
      (possibilities === maxPossibilities && existingClues < minClues)
    ) {
      bestIndex = i;
      maxPossibilities = possibilities;
      minClues = existingClues;
    }
  }

  return bestIndex;
}

/**
 * Assemble puzzle by adding clues until uniquely solvable
 */
function assemblePuzzle(
  state: GameState,
  clues: number[],
  solution: number[],
  difficulty: Difficulty
): boolean {
  const workingCopy = cloneState(state);

  // Cap difficulty for assembly to avoid guessing
  const assemblyDifficulty = Math.min(difficulty, Difficulty.EXTREME);

  while (true) {
    // Check if puzzle is uniquely solvable
    const numSolutions = solveState(workingCopy, assemblyDifficulty);

    if (numSolutions === 1) {
      // Success! Copy clues to original state
      state.nums = [...workingCopy.nums];
      state.flags = [...workingCopy.flags];
      return true;
    }

    // Select best clue to add
    const bestIndex = selectBestClue(workingCopy, clues, solution);

    if (bestIndex < 0) {
      return false; // Failed to find valid clue
    }

    // Add the clue
    const clueCode = clues[bestIndex];
    if (clueCode !== undefined) {
      canPlaceClue(workingCopy, clueCode, solution, false);
      canPlaceClue(state, clueCode, solution, false);
    }
  }
}

/**
 * Strip redundant clues
 */
function stripClues(
  state: GameState,
  clues: number[],
  solution: number[],
  difficulty: Difficulty
): void {
  for (let i = 0; i < clues.length; i++) {
    const clueCode = clues[i];
    if (clueCode === undefined) continue;

    // Check if this clue exists
    if (!removeClue(state, clueCode, true)) {
      continue;
    }

    // Remove clue temporarily
    const backup = cloneState(state);
    removeClue(state, clueCode, false);

    // Check if still uniquely solvable
    const numSolutions = solveState(state, difficulty);

    if (numSolutions !== 1) {
      // Clue is essential - put it back
      state.nums = backup.nums;
      state.flags = backup.flags;
    }
    // Otherwise clue remains removed (was redundant)
  }
}

/**
 * Generate a puzzle
 */
export function generatePuzzle(
  order: number,
  difficulty: Difficulty,
  mode: GameMode
): GameState {
  let attempts = 0;
  let currentDifficulty = difficulty;

  while (attempts < MAX_TRIES) {
    // Step 1: Generate Latin square solution
    const solution = generateLatinSquare(order);

    // Step 2: Initialize clue candidates
    const clues = initClues(order);

    // Step 3: Create empty state
    const state = createEmptyState(order, mode);
    state.solution = solution;

    // Step 4: Assemble puzzle
    if (!assemblePuzzle(state, clues, solution, currentDifficulty)) {
      attempts++;
      continue;
    }

    // Step 5: Strip redundant clues
    stripClues(state, clues, solution, currentDifficulty);

    // Step 6: Validate difficulty
    if (currentDifficulty > Difficulty.LATIN) {
      const lowerDifficulty = currentDifficulty - 1;
      const numSolutions = solveState(state, lowerDifficulty);

      if (numSolutions > 0) {
        // Puzzle solvable at lower difficulty - try again
        attempts++;

        if (attempts >= MAX_TRIES) {
          // Too many failures - downgrade and return
          currentDifficulty--;
        }
        continue;
      }
    }

    // Success!
    return state;
  }

  // Fallback: return whatever we have
  const solution = generateLatinSquare(order);
  const state = createEmptyState(order, mode);
  state.solution = solution;

  // Generate a simple puzzle with all clues visible
  const clues = initClues(order);
  for (const clue of clues) {
    canPlaceClue(state, clue, solution, false);
  }
  stripClues(state, clues, solution, Difficulty.RECURSIVE);

  return state;
}

/**
 * Generate puzzle from preset
 */
export function generateFromPreset(presetIndex: number): GameState {
  const preset = PRESETS[presetIndex] ?? PRESETS[0];
  if (!preset) {
    return generatePuzzle(4, Difficulty.EASY, GameMode.UNEQUAL);
  }
  return generatePuzzle(preset.order, preset.difficulty, preset.mode);
}
